/* eslint-disable no-restricted-syntax,no-restricted-globals */

/* global _OL_ORIGIN */
import $ from 'jquery';
import { redirect } from '@respond-framework/rudy';
import Resource, { ResourceApi } from '../../resource/Resource';
import { PAGE as PAGE_RESOURCE } from '../resourceNames';
import { UserServerActionCreators } from '../../web/data/actions/UserServerActionCreators';
import {
  LOGIN_START,
  LOGIN_SUCCESS,
  LOGIN_FAILURE,
  RESET_PASSWORD_START,
  RESET_PASSWORD_SUCCESS,
  SIGNUP_FAILURE,
  SIGNUP_START,
  CANCEL_LOGIN,
  CLEAR_RETRIES_QUEUE,
  FORGOT_PASSWORD,
  LOGIN_REQUIRED,
  SET_LOGIN_MODE,
  CANCEL_FORGOT_PASSWORD,
} from './actions';
import {
  selectHasRequestedEnrolledClassData,
  selectSignupVerificationCode,
} from './selectors';
import {
  LoginMode,
  LOGIN_ERROR_DEFAULT,
  LOGIN_ERROR_EMAIL,
  SIGNUP_ERROR_DEFAULT,
} from './Login/constants';
import {
  makeMetricsLogEvent,
  makeMetricsIdentify,
} from '../Metrics/manualEventDispatchers';
import { selectPostLoginRedirectAction } from './Login/selectors';
import {
  isLoggedIn,
  commonGetEnrolledClassData,
  setCurrentCohort,
  identifyReferral,
} from './dispatchers.common';
import { retrieveOptimizelyEnabledFeatures } from '../Optimizely/dispatchers';
import { sendUserpilotIdentify } from '../Userpilot/dispatchers';
import { OL_LANGUAGE_KEY } from '../../web/i18n/constants';

export * from './dispatchers.common';

// ================== WEB
/**
 * TODO pass the cookies in via the RFR "extra" argument (much
 * like the addReducer function) and call it from routesMap
 * so that it can also work on the server side
 */
function retryRequests(dispatch, retriesQueue) {
  for (const request of retriesQueue) {
    request();
  }
  dispatch({ type: CLEAR_RETRIES_QUEUE });
}

export function cancelLogin() {
  return (dispatch) => {
    dispatch({
      type: CANCEL_LOGIN,
    });
  };
}

export function selectForgotPassword() {
  return (dispatch) => {
    dispatch({
      type: FORGOT_PASSWORD,
    });
  };
}

export function fetchUserActiveCohortFromPage(pagePath, profileName) {
  return (dispatch) => {
    return Resource.read(PAGE_RESOURCE, pagePath, 'getUserActiveCohort', {
      profileName,
    }).then((activeCohort) => {
      dispatch(setCurrentCohort(activeCohort));
    });
  };
}

export function setLoginMode(mode) {
  return (dispatch) => {
    dispatch({
      type: SET_LOGIN_MODE,
      mode,
    });
  };
}

export function redirectAfterLogin() {
  return (dispatch, getState) => {
    const postLoginRedirectAction = selectPostLoginRedirectAction(getState());
    dispatch(redirect(postLoginRedirectAction));
  };
}

export function login(email, password, isSSO = false) {
  return (dispatch, getState) => {
    const tokenType = ['legacy'];

    if (isSSO) {
      tokenType.push('sso');
    }
    return new Promise((resolve, reject) => {
      const retriesQueue = getState().Auth.get('retriesQueue');
      const formData = {};
      dispatch({ type: LOGIN_START });
      formData.user = email;
      formData.password = password;
      // formData.logoutUser = true;
      const siteHost = _OL_ORIGIN.replace(/^http(s)?:\/\//, '');
      const currentHost = location.host;
      const redirectSSOCustomDomain = (tempAuthToken) => {
        // this workaround for safari uses the temporary token and the sso-custom-domain endpoint
        // to ensure the user is logged into the _OL_ORIGIN domain.
        const form = document.createElement('form');
        form.method = 'POST';
        form.action = `${_OL_ORIGIN}/sso-custom-domain/`;
        const authTokenInput = document.createElement('input');
        authTokenInput.type = 'hidden';
        authTokenInput.name = 'auth_token';
        authTokenInput.value = tempAuthToken;
        form.appendChild(authTokenInput);
        const redirectInput = document.createElement('input');
        redirectInput.type = 'hidden';
        redirectInput.name = 'redirect';
        redirectInput.value = `${location.href}`;
        form.appendChild(redirectInput);

        document.body.appendChild(form);
        form.submit();
      };
      const onLoginComplete = (response) => {
        dispatch(makeMetricsIdentify(response.user_id));
        dispatch(retrieveOptimizelyEnabledFeatures(response.user_id));

        // Hack to keep the User flux store up to date so that notifications still works
        // (Remove when notifications frontend is refactored into react/redux)
        // (Move to auth store initialiser when this is done via API instead of DOM)
        UserServerActionCreators.setCurrentUser(response.user.profileName);
        UserServerActionCreators.updateUser(response.user);

        window.localStorage.setItem(
          OL_LANGUAGE_KEY,
          response.user.preferredLanguage
        );

        identifyReferral(response.user.userId);
        dispatch({ type: LOGIN_SUCCESS, user: response.user });
        dispatch(sendUserpilotIdentify());
        resolve();
        if (retriesQueue.size > 0) {
          retryRequests(dispatch, retriesQueue);
        }
      };
      const onLoginFailure = (response) => {
        dispatch({ type: LOGIN_FAILURE, response });
        ResourceApi.lookup('user', 'getUserForUserName', {
          userName: email,
        }).then(
          (profileName) => {
            if (profileName) {
              reject(new Error(LOGIN_ERROR_DEFAULT));
            } else {
              reject(new Error(LOGIN_ERROR_EMAIL));
            }
          },
          () => reject(new Error(LOGIN_ERROR_DEFAULT))
        );
      };
      if (siteHost === currentHost) {
        // same domain
        formData.tokenType = tokenType.join('-');
        const xhr = $.post(`${_OL_ORIGIN}/json/auth/`, formData, null, 'JSON');
        xhr.done(onLoginComplete);
        xhr.fail(onLoginFailure);
      } else {
        // cross-domain
        // log in to OpenLearning.com domain and get a temporary
        // token to share this session with the current domain
        formData.tokenType = tokenType.concat(['temporary']).join('-');
        const olXhr = $.post('/json/auth/', formData, null, 'JSON');
        olXhr.done((response) => {
          const tempAuthToken = response.auth_token;
          const crossDomainLoginData = {
            source: 'temporary',
            tokenType: 'legacy',
            auth_token: tempAuthToken,
          };

          const crossDomainXhr = $.ajax({
            type: 'POST',
            url: `${_OL_ORIGIN}/json/auth/`,
            crossDomain: true,
            data: crossDomainLoginData,
            dataType: 'json',
            xhrFields: {
              withCredentials: true,
            },
          });
          crossDomainXhr.done((response) => {
            onLoginComplete(response);
            // For safari browsers which will prevent session cookie from being set.
            if (
              navigator.vendor &&
              navigator.vendor.match(/apple/i) &&
              typeof document.hasStorageAccess === 'function'
            ) {
              redirectSSOCustomDomain(tempAuthToken);
            }
          });
          crossDomainXhr.fail(onLoginFailure);
        });
        olXhr.fail(onLoginFailure);
      }
    });
  };
}

export function cancelForgotPassword() {
  return (dispatch) => {
    dispatch({
      type: CANCEL_FORGOT_PASSWORD,
    });
  };
}

export function loginRequired(request, mode = LoginMode.signup) {
  return (dispatch, getState) => {
    if (isLoggedIn(getState())) {
      request();
    } else {
      dispatch({ type: LOGIN_REQUIRED, request, mode });
      dispatch(makeMetricsLogEvent('LoginModal-Opened', {}));
    }
  };
}

export function logout() {
  return (dispatch, getState) => {
    return new Promise((resolve) => {
      const state = getState();
      if (isLoggedIn(state)) {
        const formData = {};
        formData.user = state.Auth.get('currentUser');
        formData.logoutEverywhere = true;
        const xhr = $.post('/json/auth/', formData, null, 'JSON');
        xhr.done(() => {
          // log out current domain (we shouldn't need this, but do it for now)
          $.post(`${_OL_ORIGIN}/json/auth/`, formData, null, 'JSON');
          dispatch(retrieveOptimizelyEnabledFeatures(''));
          dispatch(sendUserpilotIdentify());
          resolve();
        });
        // pass silently on logout error
      } else {
        resolve();
      }
    });
  };
}

export function resetPassword(email) {
  return (dispatch) => {
    dispatch({ type: RESET_PASSWORD_START });
    return Resource.execute('user', 'notifyPasswordReset', {
      email,
      userId: null,
    }).then((response) => {
      dispatch({ type: RESET_PASSWORD_SUCCESS, response });
      return response;
    });
  };
}

export function signUp(
  email,
  password,
  fullName,
  subscribe,
  managedByInstitution,
  sso = false,
  recaptchaToken = null,
  profileSummary = '',
  avatar = null
) {
  return (dispatch, getState) => {
    return new Promise((resolve, reject) => {
      dispatch({ type: SIGNUP_START });

      const verificationCode = selectSignupVerificationCode(getState());
      const formData = {};
      formData.fullName = fullName;
      formData.email = email;
      formData.password = password;
      formData.emailAddressConfirm = email;
      // TODO
      formData.isNewsSubscribed = subscribe;
      formData.sso = sso;
      formData.profileSummary = profileSummary;
      formData.signUpFrom = 'web';
      formData.acceptTerms = 'true';
      formData.verificationCode = verificationCode;
      formData.recaptchaToken = recaptchaToken;

      // pass the preferred language
      formData.preferredLanguage = window.localStorage.getItem(OL_LANGUAGE_KEY);

      if (managedByInstitution) {
        formData.managedByInstitution = managedByInstitution;
      }

      Resource.create('user', formData, {}, [], avatar)
        .then(() => {
          // Note: this won't be logged to Segment since the Segment is not
          // loaded on the page when the sign up happens, since we only
          // load Segment for signed in users. So commenting this out for
          // now.
          // dispatch(makeMetricsLogEvent('GAAccount-Created'));
          resolve();
        })
        .fail((error, response) => {
          if (response.error === 'Invalid') {
            // A bit brittle ...
            // depends on response.reason field names matching exactly
            // with the form field names [emailAddress, password, fullName]
            reject(response.reason);
          } else {
            // eslint-disable-next-line prefer-promise-reject-errors
            reject({ _error: SIGNUP_ERROR_DEFAULT });
          }
          dispatch({
            type: SIGNUP_FAILURE,
            response,
          });
        });
    });
  };
}

export function getEnrolledClassData() {
  return (dispatch, getState) => {
    if (!selectHasRequestedEnrolledClassData(getState())) {
      dispatch(commonGetEnrolledClassData());
    }
  };
}

export function signUserData(payload) {
  return () => {
    return Resource.lookup('auth', 'signUserData', { payload });
  };
}
